import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import Button from 'react-bootstrap/Button'

import { buildOfferExport } from './buildOfferExport'
import { downloadOfferExcel } from './offerExportExcel'
import { downloadOfferPdf } from './offerExportPdf'

import './offerExport.scss'

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 3a1 1 0 0 1 1 1v9.59l2.3-2.3a1 1 0 1 1 1.4 1.42l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 1.4-1.42l2.3 2.3V4a1 1 0 0 1 1-1Zm-7 14a1 1 0 0 1 1 1v1h12v-1a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1Z"
      />
    </svg>
  )
}

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6.7 9.3a1 1 0 0 1 1.4 0L12 13.17l3.9-3.88a1 1 0 1 1 1.4 1.42l-4.6 4.58a1 1 0 0 1-1.4 0L6.7 10.7a1 1 0 0 1 0-1.4Z"
      />
    </svg>
  )
}

export default function OfferExportMenu({
  context,
  offerId,
  variant = 'header',
}) {
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const focusedRef = useRef(false)
  const radioName = useId()
  const menuId = useId()
  const [open, setOpen] = useState(false)
  const [format, setFormat] = useState('pdf')
  const [busy, setBusy] = useState(false)
  const [coords, setCoords] = useState(null)

  const close = (restoreFocus = true) => {
    setOpen(false)
    if (restoreFocus) triggerRef.current?.focus()
  }

  useLayoutEffect(() => {
    if (!open) return undefined
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(400, window.innerWidth - 16)
      let left = rect.right - width
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
      let top = rect.bottom + 8
      const menuHeight = menuRef.current?.offsetHeight || 280
      if (top + menuHeight > window.innerHeight - 8) {
        top = Math.max(8, rect.top - menuHeight - 8)
      }
      setCoords({ top, left, width })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    setFormat('pdf')

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close()
      }
    }
    const onPointerDown = (event) => {
      if (menuRef.current?.contains(event.target)) return
      if (triggerRef.current?.contains(event.target)) return
      close(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      focusedRef.current = false
      return
    }
    if (!coords || focusedRef.current) return
    focusedRef.current = true
    menuRef.current?.querySelector('input[type="radio"]:checked')?.focus()
  }, [open, coords])

  const onDownload = async () => {
    setBusy(true)
    try {
      const report = buildOfferExport({ ...context, offerId })
      if (!report.offers.length) {
        window.alert('There are no offers to export.')
        return
      }
      if (format === 'excel') await downloadOfferExcel(report)
      else await downloadOfferPdf(report)
      close()
    } catch (error) {
      console.error(error)
      window.alert(error?.message || 'Could not export offers.')
    } finally {
      setBusy(false)
    }
  }

  const menu =
    open && coords
      ? createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className="offer-export__menu"
            role="dialog"
            aria-label="Offer breakdown"
            style={{ top: coords.top, left: coords.left, width: coords.width }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="offer-export__title">Offer breakdown</h2>
            <p className="offer-export__description">
              Each price level with its fees, tax, buyer total and your payout,
              in one table per offer.
            </p>
            <p className="offer-export__label">Format</p>
            <label className="offer-export__option">
              <input
                type="radio"
                name={radioName}
                value="pdf"
                checked={format === 'pdf'}
                onChange={() => setFormat('pdf')}
              />
              <span>
                <span className="offer-export__option-title">PDF report</span>
                <span className="offer-export__option-desc">
                  Landscape summary, one table per offer. Ready to share or
                  print.
                </span>
              </span>
            </label>
            <label className="offer-export__option">
              <input
                type="radio"
                name={radioName}
                value="excel"
                checked={format === 'excel'}
                onChange={() => setFormat('excel')}
              />
              <span>
                <span className="offer-export__option-title">
                  Excel workbook (.xlsx)
                </span>
                <span className="offer-export__option-desc">
                  One row per price level, with fee and payout columns.
                </span>
              </span>
            </label>
            <div className="offer-export__actions">
              <Button
                variant="outline-light"
                onClick={() => close()}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button onClick={onDownload} disabled={busy}>
                {busy ? 'Downloading…' : 'Download'}
              </Button>
            </div>
          </div>,
          document.body
        )
      : null

  return (
    <div className="offer-export" onClick={(event) => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        className={`offer-export__trigger${variant === 'card' ? ' offer-export__trigger--card' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
      >
        <DownloadIcon />
        Export
        <ChevronIcon />
      </button>
      {menu}
    </div>
  )
}
